/**
 * PageEntityContext — parties NON-COMPOSANTS : l'objet de contexte et ses deux
 * accesseurs (`useCurrentPageEntity`, `usePageLinkedEntity`).
 *
 * Extraites de `PageEntityContext.tsx` pour que ce module-là n'exporte plus QUE
 * son composant provider : Fast Refresh ne fonctionne pas sur un fichier qui
 * mélange composants et exports ordinaires
 * (`react-refresh/only-export-components`). Déplacement PUR — aucun changement
 * de comportement, de signature ni de nom.
 */

import { createContext, useCallback, useContext, useEffect } from 'react'

import { isLinkableType, linkedEntityHref, type LinkedEntityValue } from '@/lib/linkedEntity'

interface PageEntityContextValue {
    /** Fiche affichée par la page courante (null hors d'une fiche de détail). */
    pageEntity: LinkedEntityValue | null
    setPageEntity: (value: LinkedEntityValue | null) => void
}

export const PageEntityContext = createContext<PageEntityContextValue | null>(null)

/** Lecteur : la fiche de la page courante (pour le pré-remplissage du lien). */
export function useCurrentPageEntity(): LinkedEntityValue | null {
    // Contexte optionnel : hors AppLayout (ex. pages publiques), pas de fiche.
    return useContext(PageEntityContext)?.pageEntity ?? null
}

/**
 * Déclare la fiche affichée par une page de détail. À appeler avec les données
 * déjà chargées (id/nom) ; passer `id` falsy pendant le chargement enregistre
 * `null`. Nettoyé automatiquement au démontage. Le `href` est dérivé du registre
 * de recherche (source unique de navigation).
 */
export function usePageLinkedEntity(
    type: string,
    id: string | null | undefined,
    label: string | null | undefined,
): void {
    const ctx = useContext(PageEntityContext)
    const setPageEntity = ctx?.setPageEntity
    const set = useCallback(
        (v: LinkedEntityValue | null) => setPageEntity?.(v),
        [setPageEntity],
    )
    useEffect(() => {
        if (id && isLinkableType(type)) {
            set({ type, id, label: label ?? '', href: linkedEntityHref(type, id) ?? '' })
        } else {
            set(null)
        }
        return () => set(null)
    }, [type, id, label, set])
}
